import {
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect,
  useRef,
} from "react";
import { message } from "antd";
import { AppContext } from "../AppContextProvider";
import dataService from "../services/dataservice";

// Saved-search persistence for the deals list. Backed by the shared
// /api/app-share/* endpoints (de-hub de_api/app_share). Each saved search is
// scoped by (user_id, app_name, page). user_id="global" rows are shared with
// every teammate, so an admin can publish a search like "Open in Blaine
// County" org-wide.
//
// The handlers match WigFilterPanel's saved-search callbacks:
//   onSaveSearch(name, query, options?)       -> handleSaveSearch
//   onUpdateSearch(id, name, query, options?) -> handleUpdateSearch
//   onDeleteSearch(id)                        -> handleDeleteSearch
// `options` ({ forAll }) only arrives when the panel has `allowSaveForAll`.
// The panel never edits `savedSearches` itself, so every handler keeps the
// list in sync here.
//
// Where the boundary is: de-hub#332 makes the server stamp the owner from the
// token (a body user_id matters only when it is "global"), allow global writes
// to BD.Admin only (403 otherwise), 404 other users' rows, and scope the list
// to the caller's own rows plus the global ones (none for a broker agent).
// That enforcement exists only once de-hub#332 is deployed AND
// APP_SHARE_PROTECTED_APPS on the de-hub app service includes broker-deals.
// Until then `canSaveForAll` is the only thing keeping "Save for all" to
// admins, and it is a UI gate, not protection.
const APP_NAME = "broker-deals";
const PAGE = "deals_list";
export const GLOBAL_USER_ID = "global";

const FOR_ALL_FORBIDDEN = "Only admins can save searches for all users.";

/** Status code of a failed dataService call, if any. */
const statusOf = (error) => error?.response?.status;

/** Message for a failed write. `global` = the write touched a shared search. */
const writeErrorText = (error, { global, verb }) => {
  const status = statusOf(error);
  if (status === 403) {
    return global
      ? FOR_ALL_FORBIDDEN
      : `You don't have permission to ${verb} this saved search.`;
  }
  if (status === 409) {
    return "A saved search with that name already exists.";
  }
  return `Failed to ${verb} the saved search. Please try again.`;
};

/** Log a failure without the axios config (it carries the bearer token). */
const warn = (label, error) => {
  console.warn(label, statusOf(error) ?? error?.message ?? "unknown error");
};

const sameId = (a, b) => String(a) === String(b);
const sameName = (a, b) =>
  typeof a === "string" &&
  typeof b === "string" &&
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * @param {object} [options]
 * @param {boolean} [options.canSaveForAll=false] — may this user publish and
 *   change shared ("global") searches. When false, shared searches come out
 *   `readOnly` so the panel offers no Update/Delete for them. Applied when the
 *   list is read, never stored, so a role change can't leave stale flags.
 */
export function useSavedSearches({ canSaveForAll = false } = {}) {
  const { ensureToken, user } = useContext(AppContext);

  const baseInitialQuery = useMemo(
    () => ({
      combinator: "and",
      rules: [],
    }),
    []
  );

  // Stored entries are role-independent: { id, name, query, userId, isDefault }.
  const [rows, setRows] = useState([]);
  const [savedSearchesReady, setSavedSearchesReady] = useState(false);
  const [defaultSearchId, setDefaultSearchId] = useState(null);
  const [initialQuery, setInitialQuery] = useState(() =>
    structuredClone(baseInitialQuery)
  );

  // Handlers read the committed list from here, so a callback created before
  // the last render still sees the current entries. Written only by this
  // effect, never from a state updater.
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // Only the newest fetch may write the list; an older response that lands
  // late is dropped.
  const fetchSeq = useRef(0);

  /** Stored entry -> the panel's SavedSearchItem for THIS user. */
  const decorate = useCallback(
    (row) => {
      const shared = row.userId === GLOBAL_USER_ID;
      return { ...row, shared, readOnly: shared && !canSaveForAll };
    },
    [canSaveForAll]
  );

  const savedSearches = useMemo(() => rows.map(decorate), [rows, decorate]);

  // Tolerate the older { query: {...} } shape some apps stored alongside the
  // raw RuleGroupType. Strips it back to the bare {combinator, rules} the
  // query-builder expects.
  const normalizeSavedSearchQuery = useCallback(
    (raw) => {
      const fallback = () => structuredClone(baseInitialQuery);

      if (!raw) return fallback();

      let parsed = raw;
      if (typeof raw === "string") {
        try {
          parsed = JSON.parse(raw);
        } catch {
          console.error("Failed to parse a saved search query.");
          return fallback();
        }
      }

      if (
        parsed &&
        typeof parsed === "object" &&
        !Array.isArray(parsed) &&
        parsed.query &&
        typeof parsed.query === "object"
      ) {
        parsed = parsed.query;
      }

      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return fallback();
      }

      return structuredClone({
        ...parsed,
        combinator:
          typeof parsed.combinator === "string"
            ? parsed.combinator
            : baseInitialQuery.combinator,
        rules: Array.isArray(parsed.rules) ? parsed.rules : [],
      });
    },
    [baseInitialQuery]
  );

  /** Server row -> stored entry. */
  const toRow = useCallback(
    (item, index = 0) => {
      const nameFromApi =
        typeof item?.name === "string" ? item.name.trim() : "";
      return {
        id: item?.id,
        name: nameFromApi || `Saved Search ${index + 1}`,
        query: normalizeSavedSearchQuery(item?.data ?? item?.query),
        userId: item?.user_id,
        isDefault: item?.is_default ?? false,
      };
    },
    [normalizeSavedSearchQuery]
  );

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid;
    if (!userOid) throw new Error("OID not found in claims.");
    const myToken = await ensureToken();
    return { myToken, userOid };
  }, [ensureToken, user?.idTokenClaims?.oid]);

  const fetchSavedSearches = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      const { myToken, userOid } = await getAuth();

      // This user's searches AND the shared ("global") ones. Once de-hub#332
      // is live the server scopes the list itself; the filter just keeps the
      // request honest about what it wants.
      const filters = {
        combinator: "and",
        rules: [
          { field: "app_name", operator: "eq", value: APP_NAME },
          { field: "page", operator: "eq", value: PAGE },
          {
            combinator: "or",
            rules: [
              { field: "user_id", operator: "eq", value: user?.username },
              { field: "user_id", operator: "eq", value: GLOBAL_USER_ID },
            ],
          },
        ],
      };

      const response = await dataService.searchEndpoint(
        "/app-share/list",
        myToken,
        userOid,
        { filters }
      );
      if (seq !== fetchSeq.current) return;

      const items = response?.data?.data;
      if (!Array.isArray(items)) {
        throw new Error("Invalid /app-share/list response.");
      }

      // Rows without a server id cannot be updated or deleted; the panel
      // would skip them anyway.
      const next = items
        .map((item, index) => toRow(item, index))
        .filter((s) => s.id != null);

      const defaultSearch = next.find((s) => s.isDefault);
      setDefaultSearchId(defaultSearch ? defaultSearch.id : null);
      setInitialQuery(
        defaultSearch
          ? structuredClone(defaultSearch.query)
          : structuredClone(baseInitialQuery)
      );
      setRows(next);
    } catch (error) {
      if (seq !== fetchSeq.current) return;
      warn("Error fetching saved searches:", error);
    } finally {
      if (seq === fetchSeq.current) setSavedSearchesReady(true);
    }
  }, [getAuth, user?.username, toRow, baseInitialQuery]);

  /** Swap the entry with this id for `next` (or drop it when next is null). */
  const replaceById = useCallback((id, next) => {
    setRows((prev) =>
      next == null
        ? prev.filter((s) => !sameId(s.id, id))
        : prev.map((s) => (sameId(s.id, id) ? next : s))
    );
  }, []);

  /** Insert or replace by id, so a server row never appears twice. */
  const upsert = useCallback((row) => {
    setRows((prev) =>
      prev.some((s) => sameId(s.id, row.id))
        ? prev.map((s) => (sameId(s.id, row.id) ? row : s))
        : [...prev, row]
    );
  }, []);

  const ownerFor = useCallback(
    (forAll) => (forAll ? GLOBAL_USER_ID : user?.username),
    [user?.username]
  );

  /**
   * PUT an existing row (a stored entry). Optimistic: the entry changes at
   * once and is put back exactly as it was if the server says no.
   */
  const persistUpdate = useCallback(
    async (entry, { name, query, forAll }) => {
      replaceById(entry.id, {
        ...entry,
        name,
        query,
        userId: ownerFor(forAll),
      });

      try {
        const { myToken, userOid } = await getAuth();
        const response = await dataService.updateEndpoint(
          "/app-share/update",
          myToken,
          userOid,
          {
            id: entry.id,
            user_id: ownerFor(forAll),
            page: PAGE,
            name,
            data: query,
            app_name: APP_NAME,
          }
        );
        const row = response?.data?.data;
        if (row?.id != null) replaceById(entry.id, toRow(row));
        return true;
      } catch (error) {
        warn("Saved search update failed:", error);
        replaceById(entry.id, entry);
        message.error(
          writeErrorText(error, {
            global: forAll || entry.userId === GLOBAL_USER_ID,
            verb: "update",
          })
        );
        return false;
      }
    },
    [getAuth, ownerFor, replaceById, toRow]
  );

  const handleSaveSearch = useCallback(
    async (name, queryToSave, options) => {
      const trimmedName = (name ?? "").trim();
      if (!trimmedName) return;

      const forAll = options?.forAll === true;
      if (!forAll && !user?.username) {
        message.error("Failed to save the search. Please try again.");
        return;
      }
      const queryCopy = structuredClone(queryToSave);
      const inScope = (row) =>
        (row.userId === GLOBAL_USER_ID) === forAll &&
        !decorate(row).readOnly;

      // Same name in the same scope (mine vs shared): overwrite its query, as
      // the server's (user_id, app_name, page, name) uniqueness would
      // otherwise 409 the add.
      const existing = rowsRef.current.find(
        (s) => sameName(s.name, trimmedName) && inScope(s)
      );
      if (existing) {
        await persistUpdate(existing, {
          name: existing.name,
          query: queryCopy,
          forAll,
        });
        return;
      }

      // A new search joins the list only once the server returns its row, so
      // its id is always the server's id.
      try {
        const { myToken, userOid } = await getAuth();
        const response = await dataService.addEndpoint(
          "/app-share/add",
          myToken,
          userOid,
          {
            user_id: ownerFor(forAll),
            page: PAGE,
            name: trimmedName,
            data: queryCopy,
            tags: ["user"],
            is_default: false,
            app_name: APP_NAME,
          }
        );
        const row = response?.data?.data;
        if (row?.id != null) {
          upsert(toRow(row));
        } else {
          await fetchSavedSearches();
        }
      } catch (error) {
        if (statusOf(error) === 409) {
          // The add 409s for two different reasons, and the body carries the
          // conflicting row either way:
          //   - same NAME in this scope -> overwrite that row's query;
          //   - same QUERY under another name -> nothing to write. Updating
          //     it would rename the user's existing search, so just say so.
          const conflict = error?.response?.data?.data;
          if (conflict?.id != null && !sameName(conflict.name, trimmedName)) {
            message.info(
              `These filters are already saved as "${conflict.name}".`
            );
            return;
          }
          const target =
            (conflict?.id != null &&
              (rowsRef.current.find((s) => sameId(s.id, conflict.id)) ||
                toRow(conflict))) ||
            rowsRef.current.find(
              (s) => sameName(s.name, trimmedName) && inScope(s)
            );
          if (target && !decorate(target).readOnly) {
            if (!rowsRef.current.some((s) => sameId(s.id, target.id))) {
              upsert(target);
            }
            await persistUpdate(target, {
              name: target.name,
              query: queryCopy,
              forAll,
            });
            return;
          }
        }
        warn("Saved search save failed:", error);
        message.error(writeErrorText(error, { global: forAll, verb: "save" }));
      }
    },
    [
      user?.username,
      decorate,
      getAuth,
      ownerFor,
      persistUpdate,
      upsert,
      toRow,
      fetchSavedSearches,
    ]
  );

  const handleUpdateSearch = useCallback(
    async (id, name, queryToSave, options) => {
      const entry = rowsRef.current.find((s) => sameId(s.id, id));
      const view = entry && decorate(entry);
      if (!entry || view.readOnly) {
        message.error("That saved search can't be updated.");
        return;
      }
      const trimmedName = (name ?? "").trim() || entry.name;
      // Without `options` (no "Save for all" checkbox) the search keeps its
      // current scope.
      const forAll =
        typeof options?.forAll === "boolean" ? options.forAll : view.shared;
      if (!forAll && !user?.username) {
        message.error("Failed to update the saved search. Please try again.");
        return;
      }
      await persistUpdate(entry, {
        name: trimmedName,
        query: structuredClone(queryToSave ?? entry.query),
        forAll,
      });
    },
    [user?.username, decorate, persistUpdate]
  );

  const handleDeleteSearch = useCallback(
    async (id) => {
      const index = rowsRef.current.findIndex((s) => sameId(s.id, id));
      const entry = rowsRef.current[index];
      const view = entry && decorate(entry);
      if (!entry || view.readOnly) {
        message.error("That saved search can't be deleted.");
        return;
      }

      replaceById(entry.id, null);

      try {
        const { myToken, userOid } = await getAuth();
        await dataService.deleteEndpoint("/app-share/delete", myToken, userOid, {
          id: entry.id,
          user_id: ownerFor(view.shared),
        });
      } catch (error) {
        warn("Saved search delete failed:", error);
        // Put it back where it was.
        setRows((prev) => {
          if (prev.some((s) => sameId(s.id, entry.id))) return prev;
          const next = [...prev];
          next.splice(Math.min(index, next.length), 0, entry);
          return next;
        });
        message.error(
          writeErrorText(error, { global: view.shared, verb: "delete" })
        );
      }
    },
    [decorate, getAuth, ownerFor, replaceById]
  );

  return {
    savedSearches,
    savedSearchesReady,
    defaultSearchId,
    initialQuery,
    baseInitialQuery,
    fetchSavedSearches,
    handleSaveSearch,
    handleUpdateSearch,
    handleDeleteSearch,
  };
}
